import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  AppBar,
  Box,
  Toolbar,
  Button,
  IconButton,
  useTheme,
  Container,
  useMediaQuery
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import CollectionsIcon from '@mui/icons-material/Collections';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import NightsStayIcon from '@mui/icons-material/NightsStay';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import SettingsIcon from '@mui/icons-material/Settings';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AstroInfo from './AstroInfo';
import { config } from '../config';
import { getImageUrl } from '../utils/imageUrl';

const Navbar = () => {
  const theme = useTheme();
  const isCompactNav = useMediaQuery(theme.breakpoints.down('xl'));
  const [settings, setSettings] = useState({ icon: '' });
  const mobileIconButtonSx = {
    color: 'text.primary',
    flex: '1 1 0',
    minWidth: 0,
    width: 'auto',
    maxWidth: 42,
    height: 42,
    p: 0.45,
    borderRadius: 2,
    '& .MuiSvgIcon-root': {
      fontSize: '1.55rem',
      color: 'primary.light'
    }
  };

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await fetch(config.endpoints.settings);
        const data = await response.json();
        setSettings(data);
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };

    fetchSettings();
  }, []);

  return (
    <AppBar position="static" sx={{ 
      bgcolor: 'background.paper',
      borderBottom: 1,
      borderColor: 'primary.main',
      overflowX: 'clip'
    }}>
      <Container maxWidth="xl" disableGutters={isCompactNav} sx={{ maxWidth: '100vw', overflowX: 'clip' }}>
        <Toolbar sx={{
          display: 'flex',
          justifyContent: 'space-between',
          p: 0,
          px: { xs: 0.75, md: 3, lg: 0 },
          width: '100%',
          maxWidth: '100vw',
          minWidth: 0,
          overflowX: 'clip'
        }}>
          {/* Left section - Logo */}
          <Box sx={{ display: 'flex', alignItems: 'center', mr: { xs: 0.5, sm: 2 }, flex: '0 0 auto' }}>
            {settings.icon && (
              <Box
                component="img"
                src={getImageUrl(settings.icon)}
                alt="Logo"
                sx={{
                  height: { xs: 46, md: 50, xl: 40 },
                  width: { xs: 46, md: 50, xl: 'auto' },
                  objectFit: 'contain',
                  mr: { xs: 0, sm: 1 }
                }}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = '/placeholder-icon.png';
                }}
              />
            )}
          </Box>

          {/* Middle section - AstroInfo */}
          {!isCompactNav && (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              flex: 1
            }}>
              <AstroInfo />
            </Box>
          )}

          {/* Right section - Navigation */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 0.15, md: 0.6, xl: 1 },
            ml: { xs: 0, xl: 2 },
            flex: { xs: '1 1 auto', xl: '0 0 auto' },
            minWidth: 0,
            maxWidth: { xs: 'calc(100vw - 56px)', md: 'calc(100vw - 144px)', xl: 'none' },
            justifyContent: { xs: 'space-between', md: 'flex-end', xl: 'flex-start' },
            pr: { xs: 0, md: 3, xl: 0 },
            boxSizing: 'border-box',
            overflow: 'hidden'
          }}>
            {isCompactNav ? (
              <>
                <IconButton
                  component={Link}
                  to="/"
                  color="inherit"
                  sx={mobileIconButtonSx}
                >
                  <HomeIcon />
                </IconButton>
                <IconButton
                  component={Link}
                  to="/gallery"
                  color="inherit"
                  sx={mobileIconButtonSx}
                >
                  <CollectionsIcon />
                </IconButton>
                <IconButton
                  component={Link}
                  to="/videos"
                  color="inherit"
                  sx={mobileIconButtonSx}
                >
                  <VideoLibraryIcon />
                </IconButton>
                <IconButton
                  component={Link}
                  to="/night-chart"
                  color="inherit"
                  sx={mobileIconButtonSx}
                >
                  <NightsStayIcon />
                </IconButton>
                <IconButton
                  component={Link}
                  to="/sky-map"
                  color="inherit"
                  sx={mobileIconButtonSx}
                >
                  <TravelExploreIcon />
                </IconButton>
                <IconButton
                  component={Link}
                  to="/equipment"
                  color="inherit"
                  sx={mobileIconButtonSx}
                >
                  <SettingsIcon />
                </IconButton>
                <IconButton
                  component={Link}
                  to="/news"
                  color="inherit"
                  sx={mobileIconButtonSx}
                >
                  <NewspaperIcon />
                </IconButton>
              </>
            ) : (
              <>
                <Button
                  component={Link}
                  to="/"
                  color="inherit"
                  startIcon={<HomeIcon sx={{ color: 'primary.light' }} />}
                  size="small"
                  sx={{ 
                    '&:hover': { 
                      color: 'primary.light',
                      bgcolor: 'action.hover'
                    }
                  }}
                >
                  HOME
                </Button>
                <Button
                  component={Link}
                  to="/gallery"
                  color="inherit"
                  startIcon={<CollectionsIcon sx={{ color: 'primary.light' }} />}
                  size="small"
                  sx={{ 
                    '&:hover': { 
                      color: 'primary.light',
                      bgcolor: 'action.hover'
                    }
                  }}
                >
                  GALLERY
                </Button>
                <Button
                  component={Link}
                  to="/videos"
                  color="inherit"
                  startIcon={<VideoLibraryIcon sx={{ color: 'primary.light' }} />}
                  size="small"
                  sx={{ 
                    '&:hover': { 
                      color: 'primary.light',
                      bgcolor: 'action.hover'
                    }
                  }}
                >
                  VIDEOS
                </Button>
                <Button
                  component={Link}
                  to="/night-chart"
                  color="inherit"
                  startIcon={<NightsStayIcon sx={{ color: 'primary.light' }} />}
                  size="small"
                  sx={{ 
                    '&:hover': { 
                      color: 'primary.light',
                      bgcolor: 'action.hover'
                    }
                  }}
                >
                  NIGHT CHART
                </Button>
                <Button
                  component={Link}
                  to="/sky-map"
                  color="inherit"
                  startIcon={<TravelExploreIcon sx={{ color: 'primary.light' }} />}
                  size="small"
                  sx={{
                    '&:hover': {
                      color: 'primary.light',
                      bgcolor: 'action.hover'
                    }
                  }}
                >
                  SKY MAP
                </Button>
                <Button
                  component={Link}
                  to="/equipment"
                  color="inherit"
                  startIcon={<SettingsIcon sx={{ color: 'primary.light' }} />}
                  size="small"
                  sx={{ 
                    '&:hover': { 
                      color: 'primary.light',
                      bgcolor: 'action.hover'
                    }
                  }}
                >
                  EQUIPMENT
                </Button>
                <Button
                  component={Link}
                  to="/news"
                  color="inherit"
                  startIcon={<NewspaperIcon sx={{ color: 'primary.light' }} />}
                  size="small"
                  sx={{ 
                    '&:hover': { 
                      color: 'primary.light',
                      bgcolor: 'action.hover'
                    }
                  }}
                >
                  NEWS
                </Button>
                <IconButton
                  component={Link}
                  to="/admin"
                  color="inherit"
                  size="small"
                  sx={{ 
                    '&:hover': { 
                      color: 'primary.light'
                    }
                  }}
                >
                  <AdminPanelSettingsIcon sx={{ color: 'primary.light' }} />
                </IconButton>
              </>
            )}
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default Navbar;
